import { Button } from '@/components/ui/button'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { useDataSources } from '@/queries/data-sources/useDataSources'
import { useDeleteDataSource } from '@/queries/data-sources/useDeleteDataSource'
import { useStytchOrganization } from '@stytch/react/b2b'
import { Plus } from 'lucide-react'
import { useState } from 'react'

import { CreateDataSourceDialog } from './CreateDataSourceDialog'
import { DataSourceCard } from './data-source-card'

import type { WorkspaceDataSource } from 'honeygrid-types'

export const DataSourceList = () => {
	const { organization } = useStytchOrganization()
	const { data, isLoading } = useDataSources(organization?.organization_id)
	const { mutate: deleteDataSource } = useDeleteDataSource(organization?.organization_id)
	const [dialogOpen, setDialogOpen] = useState(false)
	const [selected, setSelected] = useState<WorkspaceDataSource | null>(null)

	return (
		<div className="flex h-full w-full flex-col gap-6 p-8">
			<div className="flex items-center justify-between">
				<h2 className="text-2xl font-semibold">Data Sources</h2>
				<Button onClick={() => setDialogOpen(true)} size="sm" className="cursor-pointer">
					<Plus className="mr-2 h-4 w-4" /> Add Data Source
				</Button>
			</div>

			{/* CONTENT */}
			{isLoading ? (
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
					{Array.from({ length: 6 }).map((_, i) => (
						<Skeleton key={i} className="h-32 w-full" />
					))}
				</div>
			) : data && data.length > 0 ? (
				<div className="grid flex-1 grid-cols-1 gap-4 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
					{data.map((ds) => (
						<DataSourceCard
							key={ds.id}
							dataSource={ds}
							onDelete={() => deleteDataSource(String(ds.id))}
							onViewDetails={() => setSelected(ds)}
						/>
					))}
				</div>
			) : (
				<div className="flex flex-1 flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center text-muted-foreground">
					<p className="mb-2 text-lg font-medium">No data sources yet</p>
					<p className="mb-6 text-sm">Add custom data to start enriching your workspace.</p>
					<Button onClick={() => setDialogOpen(true)} size="sm" className="cursor-pointer">
						<Plus className="mr-2 h-4 w-4" /> Add Data Source
					</Button>
				</div>
			)}

			<CreateDataSourceDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />

			{/* DETAILS SHEET */}
			<Sheet open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
				<SheetContent side="right" className="w-full max-w-2xl overflow-y-auto">
					{selected && (
						<div className="p-6 space-y-6">
							<div>
								<h3 className="text-xl font-semibold truncate">{selected.name}</h3>
								<p className="text-sm text-muted-foreground mt-1">ID: {selected.id}</p>
							</div>

							<div className="space-y-4">
								<div>
									<h4 className="text-sm font-medium text-muted-foreground mb-2">DETAILS</h4>
									<div className="grid grid-cols-2 gap-4">
										<div>
											<label className="text-xs font-medium text-muted-foreground">Type</label>
											<p className="text-sm font-medium">{selected.type.toUpperCase()}</p>
										</div>
										<div>
											<label className="text-xs font-medium text-muted-foreground">Status</label>
											<p className="text-sm font-medium capitalize">
												{selected.status.replace('_', ' ')}
											</p>
										</div>
										<div className="col-span-2">
											<label className="text-xs font-medium text-muted-foreground">File Path</label>
											<p className="text-sm font-medium break-all">{selected.file_path}</p>
										</div>
									</div>
								</div>

								<div>
									<h4 className="text-sm font-medium text-muted-foreground mb-2">METADATA</h4>
									<div className="grid grid-cols-2 gap-4">
										<div>
											<label className="text-xs font-medium text-muted-foreground">Created</label>
											<p className="text-sm">{new Date(selected.created_at).toLocaleString()}</p>
										</div>
										{selected.updated_at && (
											<div>
												<label className="text-xs font-medium text-muted-foreground">Updated</label>
												<p className="text-sm">{new Date(selected.updated_at).toLocaleString()}</p>
											</div>
										)}
										{selected.completed_at && (
											<div>
												<label className="text-xs font-medium text-muted-foreground">
													Completed
												</label>
												<p className="text-sm">
													{new Date(selected.completed_at).toLocaleString()}
												</p>
											</div>
										)}
									</div>
								</div>

								<div>
									<h4 className="text-sm font-medium text-muted-foreground mb-2">
										PROCESSING METADATA
									</h4>
									<div>
										<label className="text-xs font-medium text-muted-foreground">
											Total Cells
										</label>
										<p className="text-sm font-medium">{selected.total_cells.toLocaleString()}</p>
									</div>
								</div>
							</div>
						</div>
					)}
				</SheetContent>
			</Sheet>
		</div>
	)
}
